import { useSearchParams } from 'react-router-dom';
import { Home as HomeIcon, UserCircle2, Users } from 'lucide-react';
import { PageHeader, Tabs, type TabItem } from '../../ui';
import { AskVectorHero, DashboardContent } from '../Dashboard/DashboardPage';
import { MyVectorContent } from '../MyVector/MyVectorPage';
import { TeamVectorContent } from '../TeamVector/TeamVectorPage';
import { WhoIsWorkingPanel } from '../TeamToday/TeamTodayPage';
import { resolveHomeTab, TAB_PARAM } from '../../app/consolidatedNav';

/**
 * Unified Home — the single overview surface (UX audit Top-10 #3). Consolidates
 * the four previously-separate overview pages into one page with `Me` / `Team`
 * tabs, composing the existing content components so no data logic is
 * duplicated:
 *
 *  - **Me**: personal context ({@link MyVectorContent}) + the workspace
 *    overview / recent activity ({@link DashboardContent}).
 *  - **Team**: the shared team picture ({@link TeamVectorContent}) + the one
 *    unique Team Today element, "who is working on what?" ({@link
 *    WhoIsWorkingPanel}).
 *
 * The active tab is reflected in the `?tab=` query param so deep links (and the
 * redirects from `/me`, `/team`, `/team-today`) land on the right tab. The
 * shared Ask Vector hero sits above the tabs on every view.
 */
const TAB_ITEMS: TabItem[] = [
  { id: 'me', label: 'Me', icon: <UserCircle2 className="h-4 w-4" aria-hidden="true" /> },
  { id: 'team', label: 'Team', icon: <Users className="h-4 w-4" aria-hidden="true" /> },
];

export function HomePage() {
  const [params, setParams] = useSearchParams();
  const tab = resolveHomeTab(params.get(TAB_PARAM));

  const setTab = (id: string) => {
    const next = new URLSearchParams(params);
    // `me` is the default view, so keep the URL clean by omitting the param.
    if (id === 'team') next.set(TAB_PARAM, 'team');
    else next.delete(TAB_PARAM);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <AskVectorHero />
      <PageHeader
        icon={<HomeIcon className="h-5 w-5" />}
        title="Home"
        description="Your personal and team engineering picture — goals, work, risk and activity in one place."
      />

      <div className="mb-6">
        <Tabs items={TAB_ITEMS} value={tab} onChange={setTab} />
      </div>

      {tab === 'me' ? (
        <div className="space-y-6">
          <MyVectorContent />
          <DashboardContent />
        </div>
      ) : (
        <div className="space-y-6">
          <TeamVectorContent />
          <WhoIsWorkingPanel />
        </div>
      )}
    </div>
  );
}

export default HomePage;
